import { useSearchParams } from 'react-router-dom'
import { Filter } from '../types'
import { FilterFactory } from '../filters/factory'
import { urlFiltersSchema } from '../filters/schema'
import type { SelectFilterValue } from '../filters/select'
import type { ComboboxFilterValue } from '../filters/combobox'
import type { DateFilterValue } from '../filters/date'
import type { DateRangeFilterValue } from '../filters/date-range'

type FilterValueByType<T extends Filter> =
    T extends { type: 'select' } ? SelectFilterValue :
      T extends { type: 'combobox' } ? ComboboxFilterValue :
        T extends { type: 'date' } ? DateFilterValue :
          T extends { type: 'date-range' } ? DateRangeFilterValue :
            never

type FilterValue = SelectFilterValue | ComboboxFilterValue | DateFilterValue | DateRangeFilterValue

export type UseFiltersProps = {
  key?: string
}

export function useFilters({ key = 'filters' }: UseFiltersProps = {}) {
  const [searchParams, setSearchParams] = useSearchParams()

  /**
   * Parse a filter value from URL string to its typed value
   */
  const parseFilterValue = (filter: Filter, urlValue: string | undefined) => {
    if (!urlValue) {return null}
    const handler = FilterFactory.getFilter(filter)
    return handler.parse(urlValue)
  }

  /**
   * Serialize a filter value to URL string
   */
  const serializeFilterValue = (filter: Filter, value: FilterValue): string => {
    const handler = FilterFactory.getFilter(filter)
    return handler.serialize(value)
  }

  /**
   * Set a filter value (accepts null to remove the filter)
   * @param filter - The filter to set
   * @param value - The value to set, or null to remove
   */
  const setFilter = (
    filter: Filter,
    value: FilterValue | null,
  ) => {
    const urlFilters = getUrlFilters()

    if (value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
      delete urlFilters[filter.id]
    } else {
      urlFilters[filter.id] = serializeFilterValue(filter, value)
    }

    setUrlFilters(urlFilters)
  }

  /**
   * Get a filter value (parsed from URL)
   * @param filter - The filter to get
   * @returns The parsed value of the filter, or null if not set/invalid
   */
  function getFilter<T extends Filter>(filter: T): FilterValueByType<T> {
    const urlFilters = getUrlFilters()
    return parseFilterValue(filter, urlFilters[filter.id]) as FilterValueByType<T>
  }

  /**
   * Get all filters from the URL (raw string values)
   * @returns Object with filter id as key and URL string value as value
   */
  const getUrlFilters = (): Record<string, string> => {
    try {
      const json = JSON.parse(searchParams.get(key) ?? '{}')
      return urlFiltersSchema.safeParse(json).data ?? {}
    } catch {
      return {}
    }
  }

  const getFiltersString = (): string | null => searchParams.get(key)
  /**
   * Set filters in the URL (raw string values)
   * @param filters - The filters to set
   */
  const setUrlFilters = (filters: Record<string, string>) => {
    if (Object.keys(filters).length === 0) {
      setSearchParams(prev => {
        prev.delete(key)
        return prev
      })
    } else {
      setSearchParams({
        [key]: JSON.stringify(filters),
      })
    }
  }

  /**
   * Check if any filters are set
   * @returns True if filters are set, false otherwise
   */
  const isFiltersSet = () => Object.keys(getUrlFilters()).length !== 0

  /**
   * Clear all filters
   */
  const clearFilters = () => {
    setSearchParams(prev => {
      prev.delete(key)
      return prev
    })
  }

  /**
   * Build a URL with filters by passing filter objects with their values
   * @param filters - Array of filter objects with their values set
   * @returns Query string to append to URL
   */
  const buildFilters = (filters: Array<{ filter: Filter, value: FilterValue }>) => {
    const urlFilters: Record<string, string> = {}

    filters.forEach(({ filter, value }) => {
      if (value !== null && value !== '' && !(Array.isArray(value) && value.length === 0)) {
        urlFilters[filter.id] = serializeFilterValue(filter, value)
      }
    })

    if (Object.keys(urlFilters).length === 0) {
      return ''
    }

    return JSON.stringify(urlFilters)
  }

  return {
    setFilter,
    getFilter,
    isFiltersSet,
    clearFilters,
    getFiltersString,
    buildFilters,
  }
}
